import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Tooltip,
} from "@mzc-pdc/ui";
import theme from "@styles/theme";
import { alpha } from "@mui/material";
import { IconCancel, IconOpenInNew } from "@mzc-cloudplex/icons";
import UploadAssetItem from "@components_v2/upload-asset-item/upload-asset-item";
import { StyledGrid } from "@components_v2/grid/grid.styled";
import TableCard from "../../../components_v2/card/table-card";
import CollectionThumbnail from "../../../features/thumbnail/collection-thumbnail";
import BadgeCollection from "@components_v2/badge-collection/badge-collection";
import DialogSelectCollection from "@features/dialog/dialog-select-collection";
import { updateData } from "@modules/createAsset";
import { EllipsisTypography } from "@components_v2/ellipsis-tooltip";

const CollectionColumn = [
    {
        id: "name",
        label: "Name",
        style: { custom: { width: "calc(100% - 126px)" } },
        disabled: true,
        checked: true,
        sortable: false,
        useInAutocomplete: true,
    },
    {
        id: "count",
        label: "Assets Count",
        style: { custom: { width: 54 } },
        disabled: false,
        checked: true,
        sortable: false,
        useInAutocomplete: true,
    },
];

const getCollectionTableValueByColumn = (columnId, option) => {
    if (columnId === "name")
        return (
            <TableCard
                thumbnail={<CollectionThumbnail collection={option} size={"small"} variant={"table"} />}
                name={{
                    name: (
                        <EllipsisTypography
                            variant={"body1"}
                            component={"span"}
                            noWrap
                        >
                            {option?.name}
                        </EllipsisTypography>
                    ),
                }}
            />
        );
    if (columnId === "count")
        return (
            <Grid item>
                <BadgeCollection totalCount={option?.assetTotalCount} />
            </Grid>
        );
    return null;
};

const CreateAssetFileSection = ({ pendingCreate, isOneAsset, setIsOneAsset }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [collection, setCollection] = useState();
    const [collectionModalOpen, setCollectionModalOpen] = useState(false);

    const fileCount = useSelector(({ createAsset }) => {
        const keys = Object.keys(createAsset.selectedFiles);
        if (keys.length === 0) return 0;
        return keys.reduce((acc, key) => acc + (createAsset.elements[key]?.length || 0), 0);
    });

    useEffect(() => {
        dispatch(
            updateData({
                collection: collection ? { id: collection?.id } : {},
            }),
        );
    }, [collection, dispatch]);

    const onHandleSubmitCollection = (selected) => {
        setCollection(selected);
        setCollectionModalOpen(false);
    };

    return (
        <>
            {collectionModalOpen && (
                <DialogSelectCollection
                    open
                    title={"Specify Collection"}
                    selectLabel={"Specify Here"}
                    placeholder={t("common::msg::Please search by collection name.")}
                    onSubmit={onHandleSubmitCollection}
                    actionPending={false}
                    onClose={() => setCollectionModalOpen(false)}
                    getValueByColumn={getCollectionTableValueByColumn}
                    isDisabledCheckbox={true}
                />
            )}
            <Grid container alignItems="start" flexDirection={`column`} gap={1.5}>
                <StyledGrid
                    item
                    theme={theme}
                    usePadding={fileCount > 0}
                    useBorder={fileCount > 0}
                    sx={{ mt: fileCount > 0 ? 3 : 0 }}
                >
                    <UploadAssetItem isOneAsset={isOneAsset} disabled={pendingCreate} />

                    {/*콜렉션 지정 영역*/}
                    {fileCount > 0 && (
                        <>
                            <Divider sx={{ mt: 3, mb: 2 }} />
                            <Grid container alignItems="center" width={"100%"}>
                                {collection && (
                                    <Grid
                                        container
                                        flexDirection={"row"}
                                        alignItems={"center"}
                                        sx={{
                                            px: 1.5,
                                            py: 1,
                                            backgroundColor: alpha(
                                                theme.palette.greySecondary[50],
                                                0.5,
                                            ),
                                            borderRadius: "4px",
                                            mb: 2,
                                        }}
                                    >
                                        {CollectionColumn?.map((col) => (
                                            <Grid
                                                item
                                                {...col.style?.custom}
                                                key={`dialog-create-asset-collection-${col?.id}`}
                                            >
                                                {getCollectionTableValueByColumn(col?.id, collection)}
                                            </Grid>
                                        ))}
                                        <Grid item mx={2.5}>
                                            <Tooltip title={t(`common::label::Remove`, `Remove`)}>
                                                <IconButton
                                                    size="medium"
                                                    circled
                                                    onClick={() => setCollection(null)}
                                                >
                                                    <IconCancel
                                                        size={12}
                                                        htmlColor={theme.palette.grey[700]}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                            <Button
                                animation={"wave"}
                                animationtarget="endIcon"
                                animationtype="toRight"
                                color="secondary"
                                endIcon={<IconOpenInNew size={16} />}
                                label="Button"
                                onClick={() => setCollectionModalOpen(true)}
                                size="medium"
                                type="button"
                                variant="outlined"
                                sx={{ float: "inline-end" }}
                            >
                                {t(`common::label::Specify Collection`, `Specify Collection`)}
                            </Button>
                        </>
                    )}
                </StyledGrid>
                {fileCount > 0 && (
                    <Grid container flexDirection={"row"} justifyContent={"flex-end"}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color={"secondary"}
                                    checked={!isOneAsset}
                                    onClick={() => {
                                        setIsOneAsset(!isOneAsset);
                                    }}
                                />
                            }
                            label={t(
                                `common::label::Create assets from all files individually`,
                                `Create assets from all files individually`,
                            )}
                            sx={{ ml: 1, mr: 0 }}
                        />
                    </Grid>
                )}
            </Grid>
        </>
    );
};

export default CreateAssetFileSection;
